import type { TimesheetUnlock } from '@clockwise/clients/timesheet-unlocks';
import { ApiError } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { UserAutocomplete } from '@/components/custom/user-autocomplete';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { timesheetUnlocksApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn, localTodayIso } from '@/lib/utils';

type DateMode = 'single' | 'range';

/** Field-level errors keyed by the server's field path. */
type FieldErrors = Partial<
  Record<'fromDate' | 'toDate' | 'autoEndDate' | 'userId' | 'customerId' | 'reason', string>
>;

interface FormState {
  userId: string | null;
  customerId: string | null;
  dateMode: DateMode;
  fromDate: string;
  toDate: string;
  autoEndDate: string;
  reason: string;
}

const BLANK: FormState = {
  userId: null,
  customerId: null,
  dateMode: 'single',
  fromDate: '',
  toDate: '',
  autoEndDate: '',
  reason: '',
};

/** Seed the form from an existing unlock (edit), collapsing an equal
 *  from/to range back to single-date mode. */
function fromUnlock(u: TimesheetUnlock): FormState {
  return {
    userId: u.userId,
    customerId: u.customerId,
    dateMode: u.fromDate === u.toDate ? 'single' : 'range',
    fromDate: u.fromDate,
    toDate: u.toDate,
    autoEndDate: u.autoEndDate,
    reason: u.reason ?? '',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the sheet edits this unlock (PUT); otherwise it creates (POST). */
  unlock?: TimesheetUnlock | null;
}

/**
 * Add/edit-unlock side sheet for the settings "Unlock" tab. Admins re-open a
 * scope (a customer, an employee, both, or everyone) for a date (or range)
 * until the auto-end date passes. Reuses the shared CustomerAutocomplete /
 * UserAutocomplete / DatePicker. Mirrors the CustomerFormSheet pattern (header
 * + scrollable body + sticky footer). The server is authoritative on
 * cross-field rules; field errors render inline (G12).
 */
export function TimesheetUnlockFormSheet({ open, onOpenChange, unlock }: Props) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(unlock);

  const [state, setState] = useState<FormState>(BLANK);
  const [errors, setErrors] = useState<FieldErrors>({});
  // The `updatedAt` the form was seeded from — sent back as an optimistic-
  // concurrency token so an edit can't blind-overwrite a row someone else
  // changed while the sheet was open. Null for add.
  const [seededUpdatedAt, setSeededUpdatedAt] = useState<string | null>(null);

  // On edit, refetch the row when the sheet opens so the form seeds from the
  // latest server state — the list row passed in may be stale (another admin
  // edited/revoked it, or the list is showing keepPreviousData). The passed
  // `unlock` is the placeholder while the fetch is in flight.
  const detailQuery = useQuery({
    queryKey: unlock
      ? queryKeys.timesheetUnlocks.detail(unlock.id)
      : ['timesheetUnlocks', 'detail', 'none'],
    queryFn: () => timesheetUnlocksApi.get((unlock as TimesheetUnlock).id),
    enabled: open && Boolean(unlock),
    initialData: unlock ? { data: unlock } : undefined,
  });
  const fresh = isEdit ? (detailQuery.data?.data ?? unlock ?? null) : null;

  // Reseed whenever the sheet opens, or when the edit refetch resolves with a
  // newer row (keyed on its id + updatedAt), so the form never shows stale
  // values from a prior session and picks up a concurrent server change.
  useEffect(() => {
    if (!open) return;
    if (isEdit && fresh) {
      setState(fromUnlock(fresh));
      setSeededUpdatedAt(fresh.updatedAt);
    } else {
      setState(BLANK);
      setSeededUpdatedAt(null);
    }
    setErrors({});
  }, [open, isEdit, fresh?.id, fresh?.updatedAt]);

  const patch = (next: Partial<FormState>) => setState((s) => ({ ...s, ...next }));

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        userId: state.userId,
        customerId: state.customerId,
        fromDate: state.fromDate,
        // Single mode collapses the range to one day; range sends both.
        toDate: state.dateMode === 'single' ? state.fromDate : state.toDate,
        autoEndDate: state.autoEndDate,
        reason: state.reason.trim(),
      };
      return unlock
        ? timesheetUnlocksApi.update(unlock.id, {
            ...body,
            // Optimistic-concurrency token — 409 if the row changed under us.
            expectedUpdatedAt: seededUpdatedAt ?? undefined,
          })
        : timesheetUnlocksApi.create(body);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetUnlocks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      const overlaps = res.related.overlaps.length;
      const verb = isEdit ? 'updated' : 'created';
      if (overlaps > 0) {
        toast.warning(
          `Unlock ${verb} — it overlaps ${overlaps} existing unlock${overlaps === 1 ? '' : 's'}.`,
        );
      } else {
        toast.success(`Timesheet unlock ${verb}.`);
      }
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        const next: FieldErrors = {};
        for (const fe of err.fields) {
          if (
            fe.path === 'fromDate' ||
            fe.path === 'toDate' ||
            fe.path === 'autoEndDate' ||
            fe.path === 'userId' ||
            fe.path === 'customerId' ||
            fe.path === 'reason'
          ) {
            next[fe.path] = fe.message;
          }
        }
        setErrors(next);
        toast.error('Please fix the highlighted fields.');
        return;
      }
      toast.error(
        err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} unlock.`,
      );
    },
  });

  // Client-side guard mirrors the server's required fields for immediate
  // feedback; the server stays authoritative on cross-field rules. Reason is
  // required (every unlock must record why a locked period was re-opened).
  const canSubmit =
    state.fromDate !== '' &&
    (state.dateMode === 'single' || state.toDate !== '') &&
    state.autoEndDate !== '' &&
    state.reason.trim() !== '' &&
    !mutation.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: FieldErrors = {};
    if (!state.fromDate) next.fromDate = 'Pick a date';
    if (state.dateMode === 'range' && !state.toDate) next.toDate = 'Pick an end date';
    if (
      state.dateMode === 'range' &&
      state.fromDate &&
      state.toDate &&
      state.fromDate > state.toDate
    ) {
      next.fromDate = 'From must be on or before To';
    }
    if (!state.autoEndDate) next.autoEndDate = 'Pick an auto-end date';
    if (!state.reason.trim()) next.reason = 'A reason is required';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate();
  };

  // No react-hook-form here — derive dirtiness by comparing the working state
  // against the baseline the form was seeded from (the edited row, or BLANK on
  // create).
  const baseline = isEdit && fresh ? fromUnlock(fresh) : BLANK;
  const isDirty = JSON.stringify(state) !== JSON.stringify(baseline);
  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent
          className="flex flex-col gap-0 sm:max-w-lg"
          // Don't pull focus into the first field on open — the Employee
          // Combobox opens its dropdown on focus, which is jarring when the
          // sheet (esp. the edit sheet) first appears. The user clicks in when
          // ready.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit unlock' : 'Add unlock'}</SheetTitle>
            <SheetDescription>
              Temporarily re-open a locked period so employees can edit and resubmit.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-4">
              {/* Scope — both optional. Empty = applies to everyone / every customer. */}
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <UserAutocomplete
                  value={state.userId}
                  onChange={(u) => patch({ userId: u?.id ?? null })}
                  placeholder="All employees"
                  clearable
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to unlock for all employees.
                </p>
                {errors.userId ? <p className="text-xs text-destructive">{errors.userId}</p> : null}
              </div>

              <div className="space-y-1.5">
                <Label>Customer</Label>
                <CustomerAutocomplete
                  value={state.customerId}
                  onChange={(c) => patch({ customerId: c?.id ?? null })}
                  placeholder="All customers"
                  clearable
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to unlock for all customers.
                </p>
                {errors.customerId ? (
                  <p className="text-xs text-destructive">{errors.customerId}</p>
                ) : null}
              </div>

              {/* Date — single day or an inclusive range. Same blue-chip
                  segmented toggle as the Approvals view switch and the
                  Timesheets week/day switch — not the raw shadcn `Tabs`,
                  whose active state is a cream fill (`bg-background`), not
                  the app's blue+white "selected" look. */}
              <div className="space-y-1.5">
                <Label>Dates to unlock</Label>
                <div
                  data-surface="panel"
                  data-slot="toggle-group"
                  className="inline-flex items-center gap-0 rounded-md border light:bg-card p-0.5"
                >
                  <DateModeButton
                    active={state.dateMode === 'single'}
                    onClick={() =>
                      // Clear the now-hidden end date so a stale value can't
                      // leak back if the user toggles range → single → range.
                      patch({ dateMode: 'single', toDate: '' })
                    }
                  >
                    Single date
                  </DateModeButton>
                  <DateModeButton
                    active={state.dateMode === 'range'}
                    onClick={() => patch({ dateMode: 'range' })}
                  >
                    Date range
                  </DateModeButton>
                </div>
                {state.dateMode === 'single' ? (
                  <div className="space-y-1">
                    <DatePicker
                      value={state.fromDate}
                      onChange={(v) => patch({ fromDate: v })}
                      placeholder="Pick a date"
                      ariaInvalid={Boolean(errors.fromDate)}
                    />
                    {errors.fromDate ? (
                      <p className="text-xs text-destructive">{errors.fromDate}</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="flex-1 space-y-1">
                      <DatePicker
                        value={state.fromDate}
                        onChange={(v) => patch({ fromDate: v })}
                        placeholder="From"
                        ariaInvalid={Boolean(errors.fromDate)}
                      />
                      {errors.fromDate ? (
                        <p className="text-xs text-destructive">{errors.fromDate}</p>
                      ) : null}
                    </div>
                    <div className="flex-1 space-y-1">
                      <DatePicker
                        value={state.toDate}
                        onChange={(v) => patch({ toDate: v })}
                        placeholder="To"
                        ariaInvalid={Boolean(errors.toDate)}
                      />
                      {errors.toDate ? (
                        <p className="text-xs text-destructive">{errors.toDate}</p>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-end — a date; the unlock re-locks at end of this day in the
                employee's timezone. */}
              <div className="space-y-1.5">
                <Label htmlFor="autoEndDate">Auto-end Unlock date</Label>
                <DatePicker
                  id="autoEndDate"
                  value={state.autoEndDate}
                  onChange={(v) => patch({ autoEndDate: v })}
                  placeholder="Pick an auto-end date"
                  ariaInvalid={Boolean(errors.autoEndDate)}
                />
                <p className="text-xs text-muted-foreground">
                  The period re-locks automatically at the end of this day. Today is{' '}
                  {localTodayIso()}.
                </p>
                {errors.autoEndDate ? (
                  <p className="text-xs text-destructive">{errors.autoEndDate}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reason">Reason</Label>
                <Textarea
                  id="reason"
                  value={state.reason}
                  onChange={(e) => patch({ reason: e.target.value })}
                  maxLength={1000}
                  rows={2}
                  placeholder="Why is this period being re-opened?"
                  aria-invalid={Boolean(errors.reason)}
                />
                {errors.reason ? <p className="text-xs text-destructive">{errors.reason}</p> : null}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending
                  ? isEdit
                    ? 'Saving…'
                    : 'Creating…'
                  : isEdit
                    ? 'Save changes'
                    : 'Create unlock'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

/** Matches Approvals' `ViewButton` / Timesheets' view-switch chip — the
 *  app's one segmented-toggle look, not shadcn Tabs' default styling. */
function DateModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-slot="toggle-group-item"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors',
        active
          ? 'bg-muted text-foreground light:bg-primary light:text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
